import {
  formatName,
  formatPrice,
  formatRelativeDate,
  formatTime,
  isDateAfter,
  pluralize,
  ProfileImage,
  Rating,
  RatingSize,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React, { type FC, useMemo } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { getCoachUrl } from '../../utils/coach';
import { TierBadge } from '../TierBadge';
import { OrganizationImg, OrganizationType } from '../common/OrganizationImg';

import { type VerticalCoachCardCoachFragment } from './__generated-gql-types__/VerticalCoachCard.generated';

interface Organization {
  type: OrganizationType;
  name: string;
  logoLink?: Nullable<string>;
  text: string;
}

interface VerticalCoachCardProps {
  coach: VerticalCoachCardCoachFragment;
  categoryUrn: Scalars['CategoryUrn']['output'];
  hidePrice: boolean;
}

export const VerticalCoachCard: FC<VerticalCoachCardProps> = ({
  coach,
  categoryUrn,
  hidePrice: hidePriceProp,
}) => {
  const { isLoadingUser, currentUser } = useAuth();

  const categoryInfo = useMemo(() => {
    const categoryInRelationship = coach.categoryInformationList?.find(
      (catInfo) => catInfo.category.id === categoryUrn,
    );

    return categoryInRelationship ?? coach.categoryInformationList?.[0] ?? null;
  }, [categoryUrn, coach.categoryInformationList]);

  const shouldShowCoachTier =
    !isLoadingUser && currentUser?.applicant?.newExperience;

  const hidePrice = hidePriceProp;

  const nextAvailability = coach.availability?.nextAvailableAt;

  const coachEducationExperiences = coach.educationExperiences;
  const education: Nullable<Organization> = useMemo(() => {
    const defaultSchool = coachEducationExperiences?.[0]?.school;
    const featuredSchool = coachEducationExperiences?.find(
      (education) => education.isFeatured,
    )?.school;
    const school = featuredSchool ?? defaultSchool;
    const text = school ? `Studied at ${school.name}` : '';

    return school
      ? {
          type: OrganizationType.SCHOOL,
          name: school.name,
          logoLink: school.logoLink,
          text,
        }
      : null;
  }, [coachEducationExperiences]);

  const coachProfessionalExperiences = coach.professionalExperiences;
  const experience: Nullable<Organization> = useMemo(() => {
    const defaultCompany = coachProfessionalExperiences?.[0];
    const featuredCompany = coachProfessionalExperiences?.find(
      (company) => company.isFeatured,
    );
    const experience = coachProfessionalExperiences?.find(
      (experience) =>
        experience.company.id ===
        (featuredCompany?.company ?? defaultCompany?.company)?.id,
    );
    const text = experience
      ? `Work${experience.endAt?.year ? 'ed' : 's'} at ${
          experience.company.name
        }`
      : '';

    return experience
      ? {
          type: OrganizationType.COMPANY,
          name: experience.company.name,
          logoLink: experience.company.logoLink,
          text,
        }
      : null;
  }, [coachProfessionalExperiences]);

  return (
    <Link
      className="group flex size-full flex-col justify-between rounded border border-leland-gray-stroke transition duration-75 ease-in-out hover:border-leland-gray-dark hover:shadow-md"
      href={getCoachUrl({
        coachSlug: coach.slug,
        categorySlug: categoryInfo?.category.slug,
      })}
      target="_blank"
      rel="noreferrer"
    >
      <div className="flex flex-col gap-5 rounded-t-sm p-5">
        <div className="flex justify-between">
          <div className="flex items-center justify-center gap-2">
            <ProfileImage
              className="size-16 shrink-0"
              pictureLink={coach.user.pictureLink}
              firstName={coach.user.firstName}
              lastName={coach.user.lastName}
            />
            <div>
              <p className="break-all text-lg leading-6 text-leland-gray-light group-hover:text-leland-gray-dark">
                {formatName({
                  firstName: coach.user.firstName,
                  lastName: coach.user.lastName,
                })}
              </p>
              {coach.reviewTotal > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Rating
                    totalStars={1}
                    rate={coach.averageReviewRate}
                    showRate
                    size={RatingSize.MEDIUM}
                  />
                  <p className="whitespace-nowrap text-lg text-leland-gray-extra-light">
                    {pluralize(coach.reviewTotal, 'Review')}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
          {shouldShowCoachTier ? (
            <TierBadge
              tier={coach.tier}
              goal={categoryInfo?.category.goal}
              hideTooltip
            />
          ) : null}
        </div>
        <h2 className="line-clamp-2 h-12 break-words text-lg leading-6 text-leland-gray-dark">
          {categoryInfo?.headline}
        </h2>
        {(education ?? experience) ? (
          <div className="flex h-14 flex-col gap-3">
            {education ? <Organization organization={education} /> : null}
            {experience ? <Organization organization={experience} /> : null}
          </div>
        ) : null}
      </div>
      {!hidePrice || nextAvailability ? (
        <div className="flex flex-col rounded-b-sm border-t border-leland-gray-stroke p-5 leading-5">
          {hidePrice || !coach.hourlyRate ? null : (
            <p className="text-xl text-leland-gray-dark">
              {formatPrice(coach.hourlyRate)}
              <span className="text-base text-leland-gray-light">/hr</span>
            </p>
          )}
          {nextAvailability && isDateAfter(nextAvailability, Date.now()) ? (
            <p className="mt-1 text-leland-blue">
              Available {formatRelativeDate(nextAvailability, Date.now())} at{' '}
              {formatTime(nextAvailability, { excludeTimeZone: true })}
            </p>
          ) : null}
        </div>
      ) : null}
    </Link>
  );
};

interface OrganizationProps {
  organization: Organization;
}

const Organization: FC<OrganizationProps> = ({ organization }) => {
  return (
    <div className="flex items-center gap-3">
      <OrganizationImg
        medium
        nonSizeClassName="shrink-0 rounded-full"
        name={organization.name}
        pictureLink={organization.logoLink}
        type={organization.type}
      />
      <p className="line-clamp-1 leading-5 text-leland-gray-light">
        {organization.text}
      </p>
    </div>
  );
};
